import React from 'react';
import './PropertyCard.css';

/**
 * Unified PropertyCard component for consistent display across all deal stages
 * 
 * Stages: listings | offers | under_contract | sold
 */
function PropertyCard({ 
  stage = 'listings',
  property = {},
  status = 'active',
  offer = null,
  transaction = null,
  actions = [],
  onCardClick,
  children,
  className = ''
}) {
  
  const formatPrice = (price) => {
    if (!price) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getAddress = () => {
    const parts = [property.address];
    if (property.city) parts.push(property.city);
    if (property.state) parts.push(property.state);
    if (property.zip_code) parts.push(property.zip_code);
    return parts.filter(Boolean).join(', ').replace(/, ([^,]*)$/, ' $1');
  };

  const getDisplayPrice = () => {
    if (stage === 'offers' && offer?.offer_amount) {
      return formatPrice(offer.offer_amount);
    }
    if ((stage === 'under_contract' || stage === 'sold') && transaction?.sale_price) {
      return formatPrice(transaction.sale_price);
    }
    return formatPrice(property.price);
  };

  const getStatusLabel = () => {
    const labels = {
      draft: 'DRAFT',
      active: 'ACTIVE',
      pending: 'PENDING',
      countered: 'COUNTERED',
      rejected: 'REJECTED',
      accepted: 'ACCEPTED',
      under_contract: 'UNDER CONTRACT',
      sold: 'SOLD'
    };
    return labels[status] || 'ACTIVE';
  };

  const isOutlineStatus = ['draft', 'pending', 'countered', 'rejected', 'accepted', 'under_contract', 'sold'].includes(status);
  const isActiveStatus = status === 'active';

  return (
    <div 
      className={`property-card ${stage} ${className}`}
      onClick={onCardClick}
      style={{ cursor: onCardClick ? 'pointer' : 'default' }}
    >
      {/* Children (e.g., DealTracker for under_contract) - rendered FIRST */}
      {children && (
        <div className="property-card-children">
          {children}
        </div>
      )}

      <div className="property-card-top">
        {/* Property Image */}
        <div className="property-card-image-wrapper">
          <div className="property-card-image">
            {property.images && property.images.length > 0 ? (
              <img src={property.images[0].image_url} alt={property.address} />
            ) : (
              <div className="property-card-no-image">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
                <span>No Image</span>
              </div>
            )}
          </div>
        </div>

        {/* Main Info */}
        <div className="property-card-main">
          <div className="property-card-header">
            <h3 className="property-card-address">{getAddress()}</h3>
            <div className="property-card-price-status">
              <div className="property-card-price">{getDisplayPrice()}</div>
              <div className="property-card-status-row">
                <span className={`property-card-badge ${isOutlineStatus ? 'outline' : ''} ${isActiveStatus ? 'active' : ''}`}>
                  {getStatusLabel()}
                </span>
                {/* Copy Offer Link button stays inline with badges */}
                {actions.length > 0 && actions
                  .filter(action => action.label === 'Copy Offer Link')
                  .map((action, idx) => (
                  <button
                    key={idx}
                    className={`property-card-btn-small ${action.variant || ''}`}
                    onClick={(e) => { e.stopPropagation(); action.onClick(); }}
                    disabled={action.disabled}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Action buttons row - separate from badges */}
          {actions.length > 0 && actions.filter(action => action.label !== 'Copy Offer Link' && !(status === 'pending' && action.label === 'Edit')).length > 0 && (
            <div className="property-card-actions">
              {actions
                .filter(action => action.label !== 'Copy Offer Link' && !(status === 'pending' && action.label === 'Edit'))
                .map((action, idx) => (
                <button
                  key={idx}
                  className={`property-card-btn ${action.variant || ''}`}
                  onClick={(e) => { e.stopPropagation(); action.onClick(); }}
                  disabled={action.disabled}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Info Grid - Listings */}
          {stage === 'listings' && (
            <div className="property-card-info-grid">
              <div className="info-item">
                <span className="info-label">Beds</span>
                <span className="info-value">{property.bedrooms || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Baths</span>
                <span className="info-value">{property.bathrooms || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Sqft</span>
                <span className="info-value">{property.square_feet?.toLocaleString() || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Type</span>
                <span className="info-value">{property.property_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Single Family'}</span>
              </div>
            </div>
          )}

          {/* Info Grid - Offers */}
          {stage === 'offers' && offer && (
            <>
              <div className={`property-card-info-grid ${status === 'countered' && offer.counter_amount ? 'cols-5' : ''}`}>
                <div className="info-item">
                  <span className="info-label">Buyer</span>
                  <span className="info-value">{offer.buyer_name || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Financing</span>
                  <span className="info-value">{offer.financing_type || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Earnest Money</span>
                  <span className="info-value">{offer.earnest_money ? formatPrice(offer.earnest_money) : 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Closing Date</span>
                  <span className="info-value">{offer.closing_date ? formatDate(offer.closing_date) : 'Not specified'}</span>
                </div>
                {status === 'countered' && offer.counter_amount && (
                  <div className="info-item">
                    <span className="info-label">Your Counter</span>
                    <span className="info-value">{formatPrice(offer.counter_amount)}</span>
                  </div>
                )}
              </div>
              {status === 'countered' && (
                <p className="followup-text">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Follow up with buyer's agent to discuss counter offer
                </p>
              )}
            </>
          )}

          {/* Info Grid - Under Contract */}
          {stage === 'under_contract' && (
            <div className="property-card-info-grid">
              <div className="info-item">
                <span className="info-label">Buyer</span>
                <span className="info-value">{transaction?.buyer_name || offer?.buyer_name || 'Not specified'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Financing</span>
                <span className="info-value">{transaction?.financing_type || offer?.financing_type || 'Not specified'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Earnest Money</span>
                <span className="info-value">{transaction?.earnest_money || offer?.earnest_money ? formatPrice(transaction?.earnest_money || offer?.earnest_money) : 'Not specified'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Closing Date</span>
                <span className="info-value">{transaction?.closing_date ? formatDate(transaction.closing_date) : 'Not specified'}</span>
              </div>
            </div>
          )}

          {/* Info Grid - Sold */}
          {stage === 'sold' && (
            <div className="property-card-info-grid">
              <div className="info-item">
                <span className="info-label">Beds</span>
                <span className="info-value">{property.bedrooms || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Baths</span>
                <span className="info-value">{property.bathrooms || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Sqft</span>
                <span className="info-value">{property.square_feet?.toLocaleString() || '—'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Closed</span>
                <span className="info-value">{transaction?.closed_at ? formatDate(transaction.closed_at) : '—'}</span>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default PropertyCard;
